import * as React from "react";
import { Tabs as TabsPrimitive } from "radix-ui";

import { cn } from "@/lib/utils";

// shadcn-new-york Tabs primitive — uses the unified `radix-ui` package (BUG-007).
// Phase 2 surfaces: Position detail page (5 tabs Overview/Questions/Stages/Assigned People/Audit).

export const Tabs = TabsPrimitive.Root;

export const TabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.List>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.List
    ref={ref}
    className={cn(
      // Mobile-first: tabs scroll horizontally when content exceeds viewport
      // width (Position Detail has 5 tabs; doesn't fit on iPhone-12-class
      // viewports). On desktop, `sm:justify-center` re-centers since 5 tabs
      // fit comfortably. Scrollbar hidden; the right-edge fade-mask is the
      // affordance signaling "more content this way" (Eva 2026-05-26: hidden
      // scrollbar + truncated tab without fade-cue reads as broken UI; the
      // mechanical scroll worked but the affordance was missing). Mask
      // disabled on `sm:` since 5 tabs fit and there's no overflow to hint.
      "flex h-10 items-center justify-start sm:justify-center gap-1 border-b border-[var(--talenta-border)] max-w-full overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
      "[mask-image:linear-gradient(to_right,black_85%,transparent_100%)] sm:[mask-image:none]",
      className,
    )}
    {...props}
  />
));
TabsList.displayName = TabsPrimitive.List.displayName;

export const TabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Trigger
    ref={ref}
    className={cn(
      "inline-flex items-center justify-center whitespace-nowrap px-3 py-2 text-sm font-semibold",
      "text-[var(--talenta-text-muted)] transition-colors",
      "data-[state=active]:text-[var(--talenta-text)] data-[state=active]:border-b-2 data-[state=active]:border-[var(--talenta-primary)] data-[state=active]:-mb-px",
      "focus-visible:outline-2 focus-visible:outline-offset-2 disabled:opacity-50 disabled:pointer-events-none",
      className,
    )}
    {...props}
  />
));
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName;

export const TabsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn("mt-4 focus-visible:outline-none", className)}
    {...props}
  />
));
TabsContent.displayName = TabsPrimitive.Content.displayName;
